// Modal de onboarding "Configure em 30s" para BYOK por provedor.
// Fluxo:
//   1. Explicação curta: o que é, por que precisa
//   2. Botão grande → abre aistudio.google.com/apikey em nova aba
//   3. Passos visuais numerados (login no provedor → criar chave → copiar)
//   4. Campo de paste com toggle pra mostrar/ocultar a key
//   5. Botão "Validar e salvar" → /api/student/ai-validate-key
//   6. Sucesso → salva em uiPrefs (BYOK gemini) e fecha
//
// Pode ser exibido:
//   - Como modal (aberto via prop `open` + `onClose`)
//   - Inline numa página (props sem `open`)

import { useEffect, useRef, useState } from "preact/hooks";
import {
  AlertCircle,
  ArrowRight,
  Check,
  CheckCircle2,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  Gift,
  Key,
  Loader2,
  ShieldCheck,
  Sparkles,
  X
} from "lucide-react";
import { api } from "./api";
import { useProviderCatalog } from "./aiCatalog";
import { setAiByok, uiPrefs } from "./uiPrefs";

export interface AiOnboardingProps {
  /** Quando true, renderiza como modal centralizado com backdrop. */
  asModal?: boolean;
  /** Chamado depois que a key foi validada e salva. */
  onSuccess?: () => void;
  /** Modal: chamado quando o aluno fecha sem completar. */
  onClose?: () => void;
  /** UserId do aluno que está salvando a key. Quando trocar de
   *  conta no mesmo navegador, a key é descartada automaticamente. */
  userId: string;
}

export function AiOnboarding({
  asModal = false,
  onSuccess,
  onClose,
  userId
}: AiOnboardingProps) {
  const provider = uiPrefs.value.aiProvider;
  const { providerCatalog, strategy } = useProviderCatalog(provider);
  const [key, setKey] = useState(uiPrefs.value.aiByokApiKey ?? "");
  const [show, setShow] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validated, setValidated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    // Foca o input depois do mount — aluno já chega pronto pra colar.
    setTimeout(() => inputRef.current?.focus(), 80);
  }, []);

  async function handleValidate() {
    if (!key.trim() || validating) return;
    setError(null);
    setValidated(false);
    setValidating(true);
    try {
      const resp = await api.aiValidateKey({
        provider,
        apiKey: key.trim()
      });
      if (resp.ok) {
        setAiByok(provider, key.trim(), userId);
        setValidated(true);
        setTimeout(() => {
          onSuccess?.();
        }, 600);
      } else {
        setError(
          resp.error ??
            `A chave foi rejeitada pelo provedor (status ${resp.status ?? "desconhecido"}).`
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setValidating(false);
    }
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter" && !validating && key.trim()) {
      e.preventDefault();
      void handleValidate();
    }
  }

  const body = (
    <div class="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl">
      <header class="flex items-start gap-3 border-b border-border bg-gradient-to-br from-primary/15 via-card to-card px-6 py-5">
        <div class="grid size-12 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground shadow-md shadow-primary/40 ring-1 ring-primary/40">
          <Sparkles className="size-6" strokeWidth={2.25} />
        </div>
        <div class="min-w-0 flex-1">
          <h2 class="text-xl font-bold tracking-tight">Configure a IA em 30s</h2>
          <p class="mt-1 text-sm text-muted-foreground">
            Conecte sua chave da {providerCatalog.keyLabel}. É rápido,
            anônimo e fica salvo só no seu navegador.
          </p>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            class="grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        )}
      </header>

      <div class="flex-1 overflow-y-auto scroll-thin px-6 py-5">
        <div class="space-y-5">
          {/* Banner do tier gratuito — explicito quando o provedor oferece */}
          {providerCatalog.freeTier?.available && (
            <div class="flex items-start gap-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-3">
              <div class="grid size-9 shrink-0 place-items-center rounded-md bg-emerald-500 text-white shadow-sm">
                <Gift className="size-4" strokeWidth={2.25} />
              </div>
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2 text-sm font-bold text-emerald-700 dark:text-emerald-300">
                  {providerCatalog.freeTier.label}
                  <span class="rounded bg-emerald-500/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                    Grátis
                  </span>
                </div>
                {providerCatalog.freeTier.note && (
                  <p class="mt-0.5 text-xs leading-relaxed text-emerald-900/80 dark:text-emerald-200/80">
                    {providerCatalog.freeTier.note}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* CTA principal — abre o AI Studio em nova aba */}
          <a
            href={providerCatalog.keyUrl}
            target="_blank"
            rel="noopener noreferrer"
            class="group flex items-center gap-3 rounded-lg border-2 border-primary/40 bg-primary/5 px-5 py-4 transition-all hover:border-primary hover:bg-primary/10 hover:shadow-md hover:shadow-primary/20"
          >
            <div class="grid size-10 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground transition-transform group-hover:scale-110">
              <ExternalLink className="size-5" strokeWidth={2.25} />
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2 text-sm font-bold">
                Abrir {providerCatalog.keyLabel}
                {providerCatalog.freeTier?.available && (
                  <span class="rounded bg-emerald-500/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                    Grátis
                  </span>
                )}
              </div>
              <div class="text-xs text-muted-foreground">
                {providerCatalog.keyUrl.replace("https://", "")}
              </div>
            </div>
            <ArrowRight className="size-5 shrink-0 text-primary transition-transform group-hover:translate-x-1" />
          </a>

          {/* Passos visuais — específicos pra Gemini quando aplicável */}
          <div>
            <div class="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Como pegar sua chave
            </div>
            <ol class="space-y-2">
              {provider === "gemini" ? (
                <>
                  <Step
                    num={1}
                    title="Entre com sua Conta Google"
                    body="Qualquer Gmail serve. Se aparecer um termo de uso do AI Studio, aceite — é gratuito e não pede cartão."
                  />
                  <Step
                    num={2}
                    title='Clique em "Create API key"'
                    body='Botão azul no topo direito. Selecione "Create API key in new project" se for sua primeira vez.'
                  />
                  <Step
                    num={3}
                    title="Copie a chave que aparece (começa com AIza…)"
                    body="A chave fica salva na sua conta — você pode voltar e ver depois. Cole no campo abaixo e clique em validar."
                    icon={<Copy className="size-3.5" />}
                  />
                </>
              ) : (
                <>
                  <Step
                    num={1}
                    title={`Entre na sua conta ${providerCatalog.label}`}
                    body="Se já estiver logado, você vai direto para a área de chaves."
                  />
                  <Step
                    num={2}
                    title='Crie uma nova API key'
                    body="Cada provedor chama isso de um jeito, mas o fluxo é parecido."
                  />
                  <Step
                    num={3}
                    title="Copie a chave que aparece"
                    body="Cole no campo abaixo e valide antes de salvar."
                    icon={<Copy className="size-3.5" />}
                  />
                </>
              )}
            </ol>
          </div>

          {/* Paste */}
          <div>
            <label
              for="ai-key-input"
              class="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
            >
              <Key className="size-3" />
              Cole sua API key
            </label>
            <div class="relative">
              <input
                id="ai-key-input"
                ref={inputRef}
                type={show ? "text" : "password"}
                value={key}
                placeholder="Cole sua chave aqui"
                onInput={(e) => {
                  setKey((e.target as HTMLInputElement).value);
                  setValidated(false);
                  setError(null);
                }}
                onKeyDown={onKeyDown}
                disabled={validating}
                class="w-full rounded-md border border-border bg-background px-3 py-2.5 pr-11 text-sm font-mono outline-none transition-colors focus:border-primary/60 focus:ring-1 focus:ring-primary/30 disabled:opacity-60"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                aria-label={show ? "Ocultar chave" : "Mostrar chave"}
                title={show ? "Ocultar chave" : "Mostrar chave"}
                class="absolute right-1.5 top-1/2 grid size-8 -translate-y-1/2 place-items-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
          </div>

          {/* Status */}
          {error && (
            <div class="flex items-start gap-2 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2.5 text-sm">
              <AlertCircle className="mt-0.5 size-4 shrink-0 text-red-500" />
              <div class="min-w-0 flex-1">
                <div class="font-semibold text-red-700 dark:text-red-300">
                  Chave inválida
                </div>
                <div class="text-xs text-red-600 dark:text-red-400">{error}</div>
              </div>
            </div>
          )}
          {validated && (
            <div class="flex items-center gap-2 rounded-md border border-emerald-500/50 bg-emerald-500/15 px-3 py-2.5 text-sm">
              <CheckCircle2 className="size-5 shrink-0 text-emerald-500" />
              <div class="font-semibold text-emerald-700 dark:text-emerald-300">
                Chave validada com sucesso! Salvando…
              </div>
            </div>
          )}

          {/* Privacidade */}
          <div class="rounded-md border border-border bg-background/60 px-3 py-2.5">
            <div class="mb-1 flex items-center gap-1.5 text-xs font-bold">
              <ShieldCheck className="size-3.5 text-emerald-500" />
              Privacidade
            </div>
            <ul class="space-y-1 text-xs text-muted-foreground">
              <li>
                · A chave fica salva só no seu navegador (
                <code class="font-mono text-[11px]">localStorage</code>).
              </li>
              <li>
                · Nenhuma chave fica em log do servidor — só passa pra fazer
                a chamada ao provedor e some.
              </li>
              <li>
                · Suas conversas seguem as regras do provedor escolhido,
                não pro de outra pessoa.
              </li>
              <li>
                - {providerCatalog.connectionNote ??
                  strategy?.note ??
                  "Hoje o suporte oficial aqui e por chave da plataforma ou API key propria."}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <footer class="flex items-center justify-end gap-2 border-t border-border bg-card/50 px-6 py-4">
        {onClose && !validated && (
          <button
            type="button"
            onClick={onClose}
            disabled={validating}
            class="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent disabled:opacity-60"
          >
            Cancelar
          </button>
        )}
        <button
          type="button"
          onClick={handleValidate}
          disabled={validating || !key.trim() || validated}
          class="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground shadow-md shadow-primary/30 transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {validating ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              Validando…
            </>
          ) : validated ? (
            <>
              <Check className="size-4" />
              Tudo certo
            </>
          ) : (
            <>
              <ShieldCheck className="size-4" />
              Validar e salvar
            </>
          )}
        </button>
      </footer>
    </div>
  );

  if (!asModal) return body;

  return (
    <div
      class="fixed inset-0 z-[90] grid place-items-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget && onClose) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Configurar IA"
    >
      {body}
    </div>
  );
}

function Step({
  num,
  title,
  body,
  icon
}: {
  num: number;
  title: string;
  body: string;
  icon?: any;
}) {
  return (
    <li class="flex items-start gap-3 rounded-md border border-border bg-background/60 px-3 py-2.5">
      <span class="grid size-6 shrink-0 place-items-center rounded-full bg-primary/15 text-xs font-bold text-primary ring-1 ring-primary/30">
        {num}
      </span>
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-1.5 text-sm font-semibold">
          {title}
          {icon}
        </div>
        <div class="text-xs text-muted-foreground">{body}</div>
      </div>
    </li>
  );
}
